import { FileSelect } from '@modules/common/components/FileSelect';
import { trackCohortGroupEvents } from '@modules/integrationsV2/components/CreateOrUpdateCohortGroupModalContent/CreateOrUpdateCohortGroupModalContent';
import { useSubmitFileUploadForCohortGroup } from '@modules/integrationsV2/hooks';
import { SelectedFileError } from '@modules/integrationsV2/types';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material';
import { IntegrationType } from 'generated/graphql';
import { useState } from 'react';
import { FileUploadModalContent } from './FileUploadModalContent';
import { FileUploadModalHeader } from './FileUploadModalHeader';
import { FileUploadReviewModal } from './FileUploadReviewModal';

interface FileUploadModalProps extends Omit<DialogProps, 'onClose'> {
  integrationID: string;
  integrationType: IntegrationType;
  selectedFile: File | undefined;
  selectedFileName: string;
  selectedFileError: SelectedFileError | undefined;
  CSVFileURL: string | undefined;
  s3Key: string | undefined;
  uploadProgress: number;
  isFileUploadCompleted: boolean;
  onCloseDialog: () => void;
  onFileSelect: (file: File | undefined) => void;
  isReupload?: boolean;
}

export const FileUploadModal = ({
  integrationID,
  integrationType,
  CSVFileURL,
  s3Key,
  selectedFile,
  selectedFileName,
  selectedFileError,
  onCloseDialog,
  uploadProgress,
  isFileUploadCompleted,
  onFileSelect,
  isReupload = false,
  ...props
}: FileUploadModalProps) => {
  const [isFileUploadReviewModalOpen, setIsFileUploadReviewModalOpen] =
    useState(false);

  const [
    submitFileUploadForCustomGroup,
    { loading: isSubmittingFileUploadForCustomGroup },
  ] = useSubmitFileUploadForCohortGroup({
    integrationID,
    isReupload,
    onCompleted: () => {
      onCloseDialog();
      trackCohortGroupEvents({
        eventName: 'Cohort Group - CSV File Uploaded',
        csvFileName: selectedFileName,
      });
    },
  });

  const onProceed = async () => {
    if (integrationType === IntegrationType.Cohorts) {
      submitFileUploadForCustomGroup({
        integrationID,
        fileURL: s3Key || '',
      });
    } else {
      setIsFileUploadReviewModalOpen(true);
    }
  };

  const closeFileUploadReviewModal = () => {
    setIsFileUploadReviewModalOpen(false);
    onCloseDialog();
  };

  const renderDialogActions = () => {
    if (selectedFileError) {
      return (
        <>
          <Divider light />
          <DialogActions sx={{ my: 1 }}>
            <Stack direction="row" mx={2} my={1} spacing={2}>
              <FileSelect
                id={`${integrationID}-new-upload`}
                acceptFileType=".csv"
                onFileSelect={onFileSelect}
                buttonProps={{
                  size: 'medium',
                  variant: 'contained',
                }}
                buttonLabel="Upload a New File"
              />
              <Button onClick={onCloseDialog} variant="outlined">
                Close
              </Button>
            </Stack>
          </DialogActions>
        </>
      );
    }

    if (isFileUploadCompleted) {
      return (
        <>
          <Divider light />
          <DialogActions sx={{ my: 1 }}>
            <Stack direction="row" mx={2} my={1} spacing={2}>
              <Button onClick={onCloseDialog} variant="outlined" color="error">
                Cancel
              </Button>
              <LoadingButton
                autoFocus
                variant="contained"
                onClick={onProceed}
                loading={isSubmittingFileUploadForCustomGroup}
              >
                Proceed
              </LoadingButton>
            </Stack>
          </DialogActions>
        </>
      );
    }
  };

  return (
    <>
      <Dialog
        {...props}
        fullWidth
        maxWidth="sm"
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            onCloseDialog();
          }
        }}
        PaperProps={{ elevation: 3 }}
      >
        <DialogTitle>
          <FileUploadModalHeader
            headerText={
              !selectedFileError ? 'File Upload' : 'Error with File Upload'
            }
            onCloseDialog={onCloseDialog}
          />
        </DialogTitle>
        <Divider light />
        <DialogContent>
          <FileUploadModalContent
            fileName={selectedFileName}
            selectedFileError={selectedFileError}
            uploadProgress={uploadProgress}
            isFileUploadCompleted={isFileUploadCompleted}
            integrationType={integrationType}
          />
        </DialogContent>
        {renderDialogActions()}
      </Dialog>
      {isFileUploadCompleted && (
        <FileUploadReviewModal
          isOpen={isFileUploadReviewModalOpen}
          onClose={closeFileUploadReviewModal}
          fileUploadReviewOpts={{
            integrationID,
            fileURL: CSVFileURL,
            fileName: selectedFileName,
          }}
          onFileSelect={onFileSelect}
        />
      )}
    </>
  );
};
